import type { AddAnnotationInput, Annotation, Item } from '@mystiq/contracts';
import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { keys, useMe, useWorkspaceInfo, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { Close, Comment, ICON } from '../icons.ts';
import { clockTime, timeAgo } from '../lib/format.ts';
import { ErrorLine } from './ui.tsx';

/** What a new comment points at: words, a moment, or part of a Site, on one exact version. */
export type CommentTarget = {
  anchor: NonNullable<AddAnnotationInput['anchor']>;
  /** The version the anchor belongs to; the current one when absent. */
  revisionId?: string;
  /** What is pointed at, in words: a quote or a time. */
  label: string;
};

type CommentsState = { target: CommentTarget | null; propose(target: CommentTarget | null): void };
const CommentsContext = createContext<CommentsState | null>(null);

/** Lets parts of an item page (the editor, the player, a Site) say what the next comment points at. */
export function CommentsProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<CommentTarget | null>(null);
  const propose = useCallback((next: CommentTarget | null) => {
    setTarget(next);
    if (next) document.getElementById('new-comment')?.focus({ preventScroll: true });
  }, []);
  const value = useMemo(() => ({ target, propose }), [target, propose]);
  return <CommentsContext.Provider value={value}>{children}</CommentsContext.Provider>;
}

export function useComments(): CommentsState | null {
  return useContext(CommentsContext);
}

const shorten = (text: string, max = 140) => {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
};

function anchorWords(annotation: Annotation): string | null {
  const anchor = annotation.anchor;
  if (anchor.kind === 'text' || anchor.kind === 'element') return `“${shorten(anchor.quote)}”`;
  if (anchor.kind === 'time') return anchor.endMs > anchor.startMs ? `At ${clockTime(anchor.startMs)}–${clockTime(anchor.endMs)}` : `At ${clockTime(anchor.startMs)}`;
  return null;
}

/** Where a comment stands against the version shown now. */
function versionWords(annotation: Annotation, versionName?: (revisionId: string) => string | null): string | null {
  if (annotation.onCurrentRevision) return null;
  const which = versionName?.(annotation.revisionId) ?? 'an earlier version';
  // Words a Site shows may be written by its script, which Mystiq does not run: only text comments say whether their words are still there.
  if (annotation.anchor.kind !== 'text') return `Made on ${which}.`;
  return annotation.located ? `Made on ${which}; the same words are still there.` : `Made on ${which}; those words are no longer there.`;
}

function CommentRow({
  ws,
  annotation,
  canWrite,
  canDelete,
  versionName,
  onShow,
}: {
  ws: string;
  annotation: Annotation;
  canWrite: boolean;
  canDelete: boolean;
  versionName?: ((revisionId: string) => string | null) | undefined;
  onShow?: ((annotation: Annotation) => void) | undefined;
}) {
  const api = useApi();
  const status = useWorkspaceMutation(ws, (next: 'open' | 'resolved') => api.updateAnnotation(ws, annotation.id, { status: next }));
  const remove = useWorkspaceMutation(ws, () => api.deleteAnnotation(ws, annotation.id));
  const author = annotation.author.kind === 'agent' ? `An agent for ${annotation.author.displayName ?? 'a member'}` : (annotation.author.displayName ?? 'A member');
  const pointsAt = anchorWords(annotation);
  const version = versionWords(annotation, versionName);
  return (
    <li className="comment" data-comment-status={annotation.status}>
      <p className="comment-head">
        <span className="state">{author}</span>
        <span className="meta">
          {' '}
          · {timeAgo(annotation.createdAt)}
          {annotation.status === 'resolved' ? ' · Resolved' : ''}
        </span>
      </p>
      {pointsAt && <p className="comment-quote">{pointsAt}</p>}
      {version && <p className="meta">{version}</p>}
      <p className="comment-body">{annotation.body}</p>
      <div className="row-actions">
        {onShow && (
          <button className="link-button" onClick={() => onShow(annotation)}>
            Show
          </button>
        )}
        {canWrite && (
          <button className="link-button" disabled={status.isPending} onClick={() => status.mutate(annotation.status === 'open' ? 'resolved' : 'open')}>
            {annotation.status === 'open' ? 'Resolve' : 'Reopen'}
          </button>
        )}
        {canDelete && (
          <button className="link-button" disabled={remove.isPending} onClick={() => remove.mutate(undefined)}>
            Delete
          </button>
        )}
      </div>
      <ErrorLine error={status.error ?? remove.error} />
    </li>
  );
}

/**
 * Comments on an item. Each belongs to the exact version it was made on: on
 * a later version it says so, and a comment on words says whether those words
 * are still there. Editors and owners comment; everyone reads.
 */
export function CommentsSection({
  ws,
  item,
  hint,
  versionName,
  show,
}: {
  ws: string;
  item: Item;
  /** How to point a comment at part of this item. */
  hint: string;
  versionName?: (revisionId: string) => string | null;
  /** Shows what a comment points at (a Site element), where that is possible. */
  show?: { can(annotation: Annotation): boolean; run(annotation: Annotation): void };
}) {
  const api = useApi();
  const me = useMe();
  const info = useWorkspaceInfo(ws);
  const comments = useComments();
  const list = useQuery({ queryKey: keys.annotations(ws, item.id), queryFn: () => api.annotations(ws, item.id) });
  const [body, setBody] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const lastRequest = useRef<{ payload: string; key: string } | null>(null);
  const add = useWorkspaceMutation(ws, (input: Omit<AddAnnotationInput, 'itemId'>) => api.addAnnotation(ws, item.id, input));
  const canComment = item.permittedActions.includes('comment');
  const target = comments?.target ?? null;
  const userId = me.data?.user.id;
  const all = list.data ?? [];
  const open = all.filter((annotation) => annotation.status === 'open');
  const resolved = all.filter((annotation) => annotation.status === 'resolved');
  const row = (annotation: Annotation) => (
    <CommentRow
      key={annotation.id}
      ws={ws}
      annotation={annotation}
      canWrite={canComment}
      canDelete={annotation.author.userId === userId || info.data?.role === 'owner'}
      versionName={versionName}
      onShow={show?.can(annotation) ? show.run : undefined}
    />
  );

  return (
    <section className="section" aria-labelledby="comments-heading">
      <h2 className="section-heading" id="comments-heading">
        Comments
      </h2>
      <div className="stack">
        {canComment && (
          <form
            className="comment-composer"
            onSubmit={async (event) => {
              event.preventDefault();
              const text = body.trim();
              if (!text) return;
              const input = { anchor: target?.anchor ?? { kind: 'item' as const }, body: text, ...(target?.revisionId ? { revisionId: target.revisionId } : {}) };
              // Sending the same comment again (say, after a lost response) never adds it twice.
              const payload = JSON.stringify(input);
              if (lastRequest.current?.payload !== payload) lastRequest.current = { payload, key: crypto.randomUUID() };
              const saved = await add.mutateAsync({ ...input, idempotencyKey: lastRequest.current.key }).catch(() => null);
              if (saved) {
                // Once saved, the same words again are a new comment.
                lastRequest.current = null;
                setBody('');
                comments?.propose(null);
              }
            }}
          >
            {target ? (
              <p className="comment-target">
                <span className="meta">On</span> <span className="comment-quote">{target.anchor.kind === 'time' ? target.label : `“${shorten(target.label)}”`}</span>
                <button type="button" className="icon-button" aria-label="Comment on the whole item instead" onClick={() => comments?.propose(null)}>
                  <Close size={ICON.inline} stroke={ICON.stroke} />
                </button>
              </p>
            ) : (
              <p className="meta">{hint}</p>
            )}
            <textarea
              id="new-comment"
              className="textarea textarea-short"
              aria-label="New comment"
              placeholder="Add a comment"
              maxLength={5000}
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
            <div className="row-actions">
              <button className="button" type="submit" disabled={add.isPending || !body.trim()}>
                <Comment size={ICON.nav} stroke={ICON.stroke} />
                Add comment
              </button>
            </div>
            <ErrorLine error={add.error} />
          </form>
        )}
        {list.isSuccess && !all.length && <p className="meta">No comments yet.</p>}
        {open.length > 0 && <ul className="comments">{open.map(row)}</ul>}
        {resolved.length > 0 && (
          <div className="stack">
            <div>
              <button className="link-button" aria-expanded={showResolved} onClick={() => setShowResolved((shown) => !shown)}>
                {showResolved ? 'Hide resolved comments' : `Show resolved comments (${resolved.length})`}
              </button>
            </div>
            {showResolved && <ul className="comments">{resolved.map(row)}</ul>}
          </div>
        )}
        <ErrorLine error={list.error} />
      </div>
    </section>
  );
}
